import React, { useState, useEffect, useCallback } from 'react';
import sampleSchemes from '../data/sampleSchemes';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import './TeacherDashboard.css';

const TeacherDashboard = () => {
  const [teacher, setTeacher] = useState(null);
  const [dashboardStats, setDashboardStats] = useState({
    totalEvaluations: 0,
    recentEvaluations: []
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [evaluations, setEvaluations] = useState([]);
  const [selectedFilterSubject, setSelectedFilterSubject] = useState('');
  const [selectedFilterClass, setSelectedFilterClass] = useState('');
  
  // Evaluation form states
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedClass, setSelectedClass] = useState('');
  const [students, setStudents] = useState([{
    id: 1,
    student_name: '',
    q1: 0, q2: 0, q3: 0, q4: 0, q5: 0, q6: 0, q7: 0, q8: 0,
    notes: '',
    total: 0
  }]);
  const [submitting, setSubmitting] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  
  const navigate = useNavigate();



  const handleLogout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('userInfo');
    toast.success('Logged out successfully');
    navigate('/teacher/login');
  }, [navigate]);

  const fetchTeacherData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/teacher/profile', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setTeacher(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching teacher data:', error);
      if (error.response?.status === 401) {
        handleLogout();
      }
    }
  }, [handleLogout]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/teacher/login');
      return;
    }

    fetchTeacherData();
    fetchDashboardStats();
    fetchEvaluations();
  }, [navigate, fetchTeacherData]);

  const fetchDashboardStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/teacher/dashboard-stats', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setDashboardStats(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEvaluations = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/evaluations/teacher', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setEvaluations(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching evaluations:', error);
    }
  };


  if (loading) {
    return (
      <div className="teacher-dashboard loading">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const renderOverviewTab = () => ([
    // Teacher Profile Summary (no outer card)
    loading ? (
      <div className="profile-skeleton" key="profile-summary">
        <div className="skeleton-header"></div>
        <div className="skeleton-details">
          <div className="skeleton-item"></div>
          <div className="skeleton-item"></div>
          <div className="skeleton-item"></div>
          <div className="skeleton-item"></div>
        </div>
      </div>
    ) : (
      <div className="profile-section" key="profile-summary">
        <div className="profile-header">
          <div className="profile-title">
            <h2>Profile</h2>
            {teacher?.teacher_name && <p className="profile-name">{teacher.teacher_name}</p>}
          </div>
          <button 
            className="edit-profile-btn"
            onClick={() => setActiveTab('profile')}
          >
            ✏️ Edit
          </button>
        </div>
        {(teacher?.email || teacher?.mobile_number || teacher?.specialization || teacher?.state || teacher?.teaching_experience || teacher?.levels_taught || teacher?.educational_board || teacher?.area_of_residence || teacher?.postal_pin_code) ? (
          <div className="profile-info-grid">
            <div className="info-item" data-field="contact">
              <span className="info-label">Contact</span>
              <span className="info-value">
                {teacher?.email || teacher?.mobile_number ? (
                  <div className="contact-details">
                    {teacher?.email && <div className="contact-item">{teacher.email}</div>}
                    {teacher?.mobile_number && <div className="contact-item">{teacher.mobile_number}</div>}
                  </div>
                ) : 'Not provided'}
              </span>
            </div>
            <div className="info-item" data-field="address">
              <span className="info-label">Address</span>
              <span className="info-value">
                {teacher?.area_of_residence || teacher?.state || teacher?.postal_pin_code ? (
                  <div className="address-details">
                    {teacher?.area_of_residence && <div>{teacher.area_of_residence}</div>}
                    {teacher?.state && <div>{teacher.state}</div>}
                    {teacher?.postal_pin_code && <div className="postal-code">{teacher.postal_pin_code}</div>}
                  </div>
                ) : 'Not provided'}
              </span>
            </div>
            <div className="info-item" data-field="academic">
              <span className="info-label">Academic Board and Subjects</span>
              <span className="info-value">
                {teacher?.specialization || teacher?.educational_board ? (
                  <div className="academic-details">
                    {teacher?.educational_board && (
                      <div className="academic-item">{teacher.educational_board}</div>
                    )}
                    {teacher?.specialization && (
                      <div className="academic-item">
                        {Array.isArray(teacher.specialization)
                          ? Array.from(new Set(teacher.specialization.map(s => s.trim().toLowerCase() === 'mathematics' ? 'Math' : s.trim()))).join(', ')
                          : (teacher.specialization.trim().toLowerCase() === 'mathematics' ? 'Math' : teacher.specialization)}
                      </div>
                    )}

                  </div>
                ) : 'Not provided'}
              </span>
            </div>
            <div className="info-item" data-field="teaching">
              <span className="info-label">Teaching Experience</span>
              <span className="info-value">
                {teacher?.teaching_experience || teacher?.levels_taught ? (
                  <div className="teaching-details">
                    {teacher?.teaching_experience && (
                      <div className="teaching-item">{teacher.teaching_experience} years</div>
                    )}
                    {teacher?.levels_taught && (
                      <div className="teaching-item teaching-levels-chips" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '4px' }}>
                        {(Array.isArray(teacher.levels_taught)
                          ? teacher.levels_taught
                          : (typeof teacher.levels_taught === 'string' ? teacher.levels_taught.split(',') : [])
                        ).map((level, idx) => (
                          <span key={level + idx} className="teaching-level-chip" style={{
                            background: '#f3f3f3',
                            borderRadius: '16px',
                            padding: '2px 12px',
                            fontSize: '0.95em',
                            color: '#333',
                            border: '1px solid #e0e0e0',
                            whiteSpace: 'nowrap'
                          }}>{level.trim()}</span>
                        ))}
                      </div>
                    )}
                  </div>
                ) : 'Not provided'}
              </span>
            </div>
          </div>
        ) : (
          <div className="no-profile-data">
            <div className="empty-state">
              <div className="empty-icon">👤</div>
              <h3>No profile information available</h3>
              <p>Click "Edit Profile" to add your information and complete your profile</p>
            </div>
          </div>
        )}
      </div>
    ),
    // Dashboard Stats
    <div className="stats-grid" key="stats-grid">
      <div className="stat-card" style={{ display: 'flex', alignItems: 'center', gap: '18px', minHeight: '80px', boxShadow: 'none' }}>
        <div className="stat-icon" style={{ fontSize: '2.2em', width: '48px', height: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'none', boxShadow: 'none' }}>
          📄
        </div>
        <div className="stat-content" style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <span style={{ fontWeight: 500, fontSize: '1.15em', color: '#444' }}>Total Evaluations : {dashboardStats.totalEvaluations}</span>
        </div>
      </div>
      <div className="stat-card" style={{ display: 'flex', alignItems: 'center', gap: '18px', minHeight: '80px', boxShadow: 'none' }}>
        <div className="stat-icon" style={{ fontSize: '2.2em', width: '48px', height: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'none', boxShadow: 'none' }}>
          📅
        </div>
        <div className="stat-content" style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <span style={{ fontWeight: 500, fontSize: '1.15em', color: '#444' }}>Recent Evaluations : {dashboardStats.recentEvaluations.length}</span>
        </div>
      </div>
    </div>,

    // Recent Evaluations
    dashboardStats.recentEvaluations.length > 0 && (
      <div className="recent-evaluations" key="recent-evaluations">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 18, marginBottom: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
            <span className="recent-evals-icon">📊</span>
            <span className="recent-evals-title">Recent Evaluations</span>
          </div>
          <div className="score-metric-legend">
            <span className="score-badge excellent">Excellent (≥32)</span>
            <span className="score-badge good">Good (20–31)</span>
            <span className="score-badge needs-improvement">Needs Improvement (&lt;20)</span>
          </div>
        </div>
        <div className="evaluations-list" style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
          {dashboardStats.recentEvaluations.slice(0, 5).map((evaluation, index) => {
            let score = (typeof evaluation.total === 'number' && !isNaN(evaluation.total)) ? evaluation.total :
                        (typeof evaluation.total_marks === 'number' && !isNaN(evaluation.total_marks)) ? evaluation.total_marks :
                        (typeof evaluation.overall_score === 'number' && !isNaN(evaluation.overall_score)) ? evaluation.overall_score : null;
            let maxScore = (typeof evaluation.total === 'number' && !isNaN(evaluation.total)) ? 40 :
                           (typeof evaluation.total_marks === 'number' && !isNaN(evaluation.total_marks)) ? 40 :
                           (typeof evaluation.overall_score === 'number' && !isNaN(evaluation.overall_score)) ? 10 : null;
            let scoreDisplay = (score !== null && maxScore !== null) ? `${score}/${maxScore}` : '';
            let badgeClass = score !== null && maxScore !== null
              ? (score >= 32 ? 'excellent' : score >= 20 ? 'good' : 'needs-improvement')
              : 'needs-improvement';
           // Inline style for mobile badge size
           const badgeStyle = (typeof window !== 'undefined' && window.innerWidth <= 992) ? { width: '80px', height: '40px' } : {};
            return (
              <div key={index} className="evaluation-item" style={{
                background: '#fff',
                borderRadius: 14,
                boxShadow: '0 2px 8px 0 #e3e6ff',
                padding: '18px 32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                minHeight: 70,
                fontSize: '1.05em',
                fontWeight: 700
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 700, color: '#222', minWidth: 120 }}>Name: <span style={{ fontWeight: 600 }}>{evaluation.student_name || 'Unnamed Student'}</span></span>
                  <span style={{ color: '#222', minWidth: 120, fontWeight: 700 }}>Class: <span style={{ color: '#2563eb', fontWeight: 700 }}>{evaluation.class_level || 'N/A'}</span></span>
                  <span style={{ color: '#222', minWidth: 120, fontWeight: 700 }}>Subject: <span style={{ background: '#2563eb', color: '#fff', borderRadius: 12, padding: '3px 14px', fontWeight: 700, letterSpacing: 1 }}>{(evaluation.subject || 'N/A').toString().toUpperCase()}</span></span>
                  <span style={{ color: '#222', minWidth: 120, fontWeight: 700 }}>Date: <span style={{ fontWeight: 600 }}>{evaluation.created_at ? new Date(evaluation.created_at).toLocaleDateString() : ''}</span></span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  {scoreDisplay ? (
                    <span className={`score-badge ${badgeClass}`} style={badgeStyle}>Score: {scoreDisplay}</span>
                  ) : (
                    <span className="score-badge needs-improvement" style={badgeStyle}>No Score</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    )
  ]);

  const renderEvaluationTab = () => {
    // Only show subjects the teacher is specialized in
    let normalizedSpecialization = [];
    if (teacher && teacher.specialization) {
      normalizedSpecialization = Array.isArray(teacher.specialization)
        ? teacher.specialization.map(s => s.trim().toLowerCase() === 'mathematics' ? 'Math' : s.trim())
        : [teacher.specialization.trim().toLowerCase() === 'mathematics' ? 'Math' : teacher.specialization.trim()];
    }
    // Map to display labels
    const subjectDisplayMap = { English: 'English', Math: 'Mathematics', Science: 'Science' };
    const allowedSubjects = ['English', 'Math', 'Science'];
    const subjects = allowedSubjects
      .filter(subj => normalizedSpecialization.includes(subj))
      .map(subj => subjectDisplayMap[subj]);

    // Only show class levels the teacher is allowed to teach
    const allClasses = [
      { value: 'pre-primary', label: 'Pre-Primary' },
      { value: 'primary', label: 'Primary' },
      { value: 'high-school', label: 'High School' },
      { value: 'higher-secondary', label: 'Higher Secondary' }
    ];
    let normalizedLevelsTaught = [];
    if (teacher && teacher.levels_taught) {
      if (Array.isArray(teacher.levels_taught)) {
        normalizedLevelsTaught = teacher.levels_taught.map(l => l.trim().toLowerCase());
      } else if (typeof teacher.levels_taught === 'string') {
        normalizedLevelsTaught = teacher.levels_taught.split(',').map(l => l.trim().toLowerCase());
      }
    }
    // Map class value to normalized label for comparison
    const classValueToNormalized = {
      'pre-primary': 'pre-primary',
      'primary': 'primary',
      'high-school': 'high school',
      'higher-secondary': 'higher secondary'
    };
    const classes = allClasses.filter(cls => normalizedLevelsTaught.includes(classValueToNormalized[cls.value]));

    const handleSubjectSelect = (subject) => {
      setSelectedSubject(subject);
      setCurrentStep(2);
    };

    const handleClassSelect = (classValue) => {
      setSelectedClass(classValue);
      setCurrentStep(3);
    };

    const handleStudentInputChange = (studentId, field, value) => {
      setStudents(prev => prev.map(student => {
        if (student.id === studentId) {
          const updatedStudent = { ...student, [field]: value };
          
          // Calculate total if it's a question score
          if (field.startsWith('q')) {
            const total = ['q1', 'q2', 'q3', 'q4', 'q5', 'q6', 'q7', 'q8']
              .reduce((sum, q) => sum + parseInt(updatedStudent[q] || 0), 0);
            updatedStudent.total = total;
          }
          
          return updatedStudent;
        }
        return student;
      }));
    };

    const addMoreStudents = () => {
      const newId = Math.max(...students.map(s => s.id)) + 1;
      setStudents(prev => [...prev, {
        id: newId,
        student_name: '',
        q1: 0, q2: 0, q3: 0, q4: 0, q5: 0, q6: 0, q7: 0, q8: 0,
        notes: '',
        total: 0
      }]);
    };

    const removeStudent = (studentId) => {
      if (students.length > 1) {
        setStudents(prev => prev.filter(s => s.id !== studentId));
      }
    };

    const handleSubmitEvaluations = async () => {
      setSubmitting(true);
      try {
        const token = localStorage.getItem('token');
        // Map selectedClass to DB value
        const classLevelMap = {
          'pre-primary': 'Pre-Primary',
          'primary': 'Primary',
          'high-school': 'High School',
          'higher-secondary': 'Higher Secondary'
        };
        const dbClassLevel = classLevelMap[selectedClass] || selectedClass;
        const evaluations = students.map(student => {
          const total_marks = [
            Number(student.q1) || 0,
            Number(student.q2) || 0,
            Number(student.q3) || 0,
            Number(student.q4) || 0,
            Number(student.q5) || 0,
            Number(student.q6) || 0,
            Number(student.q7) || 0,
            Number(student.q8) || 0
          ].reduce((sum, val) => sum + val, 0);
          // Ensure subject matches DB constraint: 'English', 'Math', 'Science'
          let dbSubject = selectedSubject;
          if (dbSubject === 'Mathematics') dbSubject = 'Math';
          return {
            student_name: student.student_name,
            subject: dbSubject,
            class_level: dbClassLevel, // send as class_level for backend compatibility
            question_1_score: Number(student.q1) || 0,
            question_2_score: Number(student.q2) || 0,
            question_3_score: Number(student.q3) || 0,
            question_4_score: Number(student.q4) || 0,
            question_5_score: Number(student.q5) || 0,
            question_6_score: Number(student.q6) || 0,
            question_7_score: Number(student.q7) || 0,
            question_8_score: Number(student.q8) || 0,
            total_marks,
            notes: student.notes || ''
          };
        });

        const response = await axios.post('/api/evaluations/batch', {
          evaluations
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.data.success) {
          toast.success(`Successfully submitted ${students.length} evaluation(s)!`);
          setShowConfirmation(true);
          // Reset form
          setCurrentStep(1);
          setSelectedSubject('');
          setSelectedClass('');
          setStudents([{
            id: 1,
            student_name: '',
            q1: 0, q2: 0, q3: 0, q4: 0, q5: 0, q6: 0, q7: 0, q8: 0,
            notes: '',
            total: 0
          }]);
          fetchDashboardStats();
          fetchEvaluations(); // Fetch latest evaluations after submit
          setTimeout(() => setShowConfirmation(false), 3500);
        }
      } catch (error) {
        console.error('Error submitting evaluations:', error);
        toast.error('Failed to submit evaluations');
      } finally {
        setSubmitting(false);
      }
    };

    const resetForm = () => {
      setCurrentStep(1);
      setSelectedSubject('');
      setSelectedClass('');
      setStudents([{
        id: 1,
        student_name: '',
        q1: 0, q2: 0, q3: 0, q4: 0, q5: 0, q6: 0, q7: 0, q8: 0,
        total: 0
      }]);
    };

    return (
      <div className="evaluation-content">
        {showConfirmation && (
          <div className="confirmation-message" style={{
            background: '#e6fbe6',
            border: '1.5px solid #7ed957',
            borderRadius: 12,
            padding: 18,
            margin: '24px auto',
            maxWidth: 500,
            textAlign: 'center',
            color: '#217a2c',
            fontWeight: 600,
            fontSize: '1.18em',
            boxShadow: '0 2px 12px 0 #d6ffd6',
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            justifyContent: 'center'
          }}>
            <span style={{ fontSize: '2em', marginRight: 8 }}>✅</span>
            <span>Evaluation(s) submitted successfully!</span>
          </div>
        )}
        {/* Progress Steps */}
        <div className="evaluation-progress" style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 24,
          margin: '0 0 28px 0',
          background: 'transparent',
          boxShadow: 'none',
          borderRadius: 0,
          padding: 0
        }}>
          {[{
            label: 'Select Subject',
            step: 1
          }, {
            label: 'Select Class',
            step: 2
          }, {
            label: 'Evaluate Students',
            step: 3
          }].map(({ label, step }, idx, arr) => (
            <React.Fragment key={step}>
              <div
                className={`step${currentStep === step ? ' active' : ''}${currentStep > step ? ' completed' : ''}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  background: currentStep === step ? 'linear-gradient(90deg, #6a8cff 0%, #8f5be8 100%)' : currentStep > step ? 'linear-gradient(90deg, #b2d6ff 0%, #d1bfff 100%)' : '#f3f3f8',
                  color: currentStep === step ? '#fff' : currentStep > step ? '#5a4e8c' : '#888',
                  fontWeight: 600,
                  fontSize: '1em',
                  borderRadius: 12,
                  padding: '8px 20px',
                  boxShadow: currentStep === step ? '0 2px 8px 0 #e3e6ff' : 'none',
                  border: currentStep === step ? '2px solid #6a8cff' : '2px solid #e3e6ff',
                  transition: 'all 0.2s',
                  minWidth: 120,
                  justifyContent: 'center',
                  position: 'relative',
                  zIndex: 1
                }}
              >
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 22,
                  height: 22,
                  borderRadius: '50%',
                  background: currentStep === step ? 'rgba(255,255,255,0.22)' : currentStep > step ? '#e3e6ff' : '#eaeaf5',
                  color: currentStep === step ? '#fff' : currentStep > step ? '#6a8cff' : '#aaa',
                  fontWeight: 700,
                  fontSize: '0.98em',
                  marginRight: 7,
                  border: currentStep === step ? '2px solid #fff' : '2px solid #e3e6ff',
                  boxShadow: currentStep === step ? '0 1px 4px 0 #bfcfff' : 'none',
                  transition: 'all 0.2s'
                }}>{step}</span>
                <span>{label}</span>
              </div>
              {idx < arr.length - 1 && (
                <div style={{
                  width: 28,
                  height: 3,
                  background: currentStep > step ? 'linear-gradient(90deg, #b2d6ff 0%, #d1bfff 100%)' : '#e3e6ff',
                  borderRadius: 2,
                  margin: '0 0',
                  transition: 'all 0.2s'
                }} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Step 1: Subject Selection */}
        {currentStep === 1 && (
          <div className="subject-selection">
            <h3>Select Subject to Evaluate</h3>
            <div className="subject-grid">
              {subjects.map(subject => (
                <div
                  key={subject}
                  className="subject-card"
                  onClick={() => handleSubjectSelect(subject)}
                >
                  <div className="subject-icon">
                    {subject === 'English' && '📖'}
                    {subject === 'Mathematics' && '🧮'}
                    {subject === 'Science' && '🔬'}
                  </div>
                  <h4>{subject}</h4>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Class Selection */}
        {currentStep === 2 && (
          <div className="class-selection">
            <div className="selection-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 32, gap: 16 }}>
              <button className="back-btn" onClick={() => setCurrentStep(1)} style={{ minWidth: 80 }}>
                ← Back
              </button>
              <h3 style={{ margin: 0, textAlign: 'center', flex: 1, fontSize: '1.7em', fontWeight: 700, letterSpacing: 0.5 }}>Select Class Level</h3>
              <span style={{ color: '#222', fontWeight: 500, fontSize: '1.13em', whiteSpace: 'nowrap', minWidth: 120, textAlign: 'right' }}>
                Subject: <strong>{selectedSubject}</strong>
              </span>
            </div>
            <div className="class-grid">
              {classes.map(classItem => (
                <div
                  key={classItem.value}
                  className="class-card"
                  onClick={() => handleClassSelect(classItem.value)}
                >
                  <h4>{classItem.label}</h4>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Step 3: Student Evaluation Form */}
        {currentStep === 3 && (
          <div className="student-evaluation" style={{ width: '100%', maxWidth: '1300px', margin: '0 auto', background: 'none', boxShadow: 'none', padding: '0 0 32px 0' }}>

            {/* Sample Questions, Marking Scheme and Skill Development (Dynamic) */}
            {(() => {
              const key = `${selectedSubject}|${selectedClass}`;
              const scheme = sampleSchemes[key];
              if (!scheme) return null;
              return (
                <div className="sample-scheme-section" style={{
                  margin: '32px 0 40px 0',
                  background: '#fff',
                  borderRadius: 14,
                  boxShadow: '0 2px 12px 0 #d6e2ff',
                  padding: 32,
                  width: '100%',
                  border: '1.5px solid #bfcfff'
                }}>
                  <div className="evaluation-info" style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24, background: 'transparent', boxShadow: 'none', padding: 0 }}>
                    <button className="back-btn" onClick={() => setCurrentStep(2)}>
                      ← Back
                    </button>
                    <span style={{ marginLeft: 12, color: '#333', fontSize: '1.13em', fontWeight: 500 }}>
                      Subject: <strong>{selectedSubject}</strong> | Class: <strong>{selectedClass}</strong>
                    </span>
                  </div>
                  <h4
                    style={{
                      marginBottom: 18,
                      fontSize: '1.18em',
                      fontWeight: 700,
                      letterSpacing: 0.2,
                      background: 'linear-gradient(90deg, var(--primary-color) 15%, var(--secondary-color) 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                      color: 'transparent',
                      display: 'inline-block'
                    }}
                  >Sample Questions, Marking Scheme and Skill Development</h4>
                  <div>
                    <table className="sample-scheme-table" style={{
                      width: '100%',
                      background: '#f7f8ff',
                      ...scheme.tableStyle,
                      border: '1.5px solid #bfcfff',
                      borderRadius: 10,
                      overflow: 'hidden'
                    }}>
                      <thead>
                        <tr style={{
                          background: 'linear-gradient(90deg, var(--primary-color) 15%, var(--secondary-color) 100%)',
                          color: 'white'
                        }}>
                          <th style={{ ...scheme.cellStyle, fontWeight: 700, color: 'white', background: 'transparent' }}>Info</th>
                          {[...Array(8)].map((_, i) => (
                            <th key={i} style={{ ...scheme.cellStyle, fontWeight: 700, color: 'white', background: 'transparent' }}>Q{i+1}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {scheme.rows.map((row, idx) => (
                          <tr key={row[0]} style={{ background: idx % 2 === 0 ? '#f3f6ff' : '#fff' }}>
                            {row.map((cell, i) => (
                              <td key={i} style={{ ...scheme.cellStyle, fontWeight: i === 0 ? 600 : 400, textAlign: i === 0 ? 'left' : 'center', color: '#333' }}>{cell}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })()}

            {/* Students Table */}
            {!(selectedSubject === 'English' && selectedClass === 'pre-primary') &&
              !(selectedSubject === 'Mathematics' && selectedClass === 'pre-primary') && (
                <div className="evaluation-info" style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24, background: 'transparent', boxShadow: 'none', padding: 0 }}>
                  <button className="back-btn" onClick={() => setCurrentStep(2)}>
                    ← Back
                  </button>
                  <span style={{ marginLeft: 12, color: '#333', fontSize: '1.13em', fontWeight: 500 }}>
                    Subject: <strong>{selectedSubject}</strong> | Class: <strong>{selectedClass}</strong>
                  </span>
                </div>
            )}
      <div className="students-table-container" style={{ width: '100%', margin: '0 auto', background: 'none' }}>
        <table className="students-table" style={{ borderCollapse: 'separate', borderSpacing: '0 8px', width: '100%', background: '#fff', fontSize: '0.98em' }}>
          <thead>
            <tr style={{ background: '#f6f6fa' }}>
              <th style={{ padding: '14px 16px' }}>Name</th>
              <th style={{ padding: '14px 16px' }}>Subject</th>
              <th style={{ padding: '14px 16px' }}>Class</th>
              <th style={{ padding: '14px 16px' }}>Q1 (/5)</th>
              <th style={{ padding: '14px 16px' }}>Q2 (/5)</th>
              <th style={{ padding: '14px 16px' }}>Q3 (/5)</th>
              <th style={{ padding: '14px 16px' }}>Q4 (/5)</th>
              <th style={{ padding: '14px 16px' }}>Q5 (/5)</th>
              <th style={{ padding: '14px 16px' }}>Q6 (/5)</th>
              <th style={{ padding: '14px 16px' }}>Q7 (/5)</th>
              <th style={{ padding: '14px 16px' }}>Q8 (/5)</th>
              <th style={{ padding: '14px 16px' }}>Notes </th>
              <th style={{ padding: '14px 16px' }}>Total (/40)</th>
              <th style={{ padding: '14px 16px' }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {students.map((student, idx) => (
              <tr key={student.id} style={{ background: idx % 2 === 0 ? '#f9f9fc' : '#fff' }}>
                <td style={{ padding: '12px 16px' }}>
                  <input
                    type="text"
                    placeholder="Enter student name"
                    value={student.student_name}
                    onChange={(e) => handleStudentInputChange(student.id, 'student_name', e.target.value)}
                    required
                    style={{ width: '100%', padding: '8px 10px', fontSize: '1em', borderRadius: 6, border: '1px solid #e0e0e0' }}
                  />
                </td>
                <td style={{ padding: '12px 16px', textAlign: 'center' }}>{selectedSubject}</td>
                <td style={{ padding: '12px 16px', textAlign: 'center' }}>{selectedClass}</td>
                {['q1', 'q2', 'q3', 'q4', 'q5', 'q6', 'q7', 'q8'].map(q => (
                  <td key={q} style={{ padding: '12px 10px', textAlign: 'center' }}>
                    <input
                      type="number"
                      min="0"
                      max="5"
                      value={student[q]}
                      onChange={(e) => handleStudentInputChange(student.id, q, parseInt(e.target.value) || 0)}
                      style={{ width: 48, padding: '6px 6px', fontSize: '1em', borderRadius: 6, border: '1px solid #e0e0e0', textAlign: 'center' }}
                    />
                  </td>
                ))}
                <td style={{ padding: '12px 16px' }}>
                  <input
                    type="text"
                    placeholder="Notes (optional)"
                    value={student.notes}
                    onChange={(e) => handleStudentInputChange(student.id, 'notes', e.target.value)}
                    style={{ width: '100%', padding: '8px 10px', fontSize: '1em', borderRadius: 6, border: '1px solid #e0e0e0' }}
                  />
                </td>
                <td style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 700 }}>
                  {student.total}
                </td>
                <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                  {students.length > 1 && (
                    <button
                      className="remove-btn"
                      onClick={() => removeStudent(student.id)}
                      style={{ padding: '4px 10px', borderRadius: 6, background: '#fbeaea', color: '#d33', border: 'none', fontWeight: 600, cursor: 'pointer' }}
                    >
                      Remove
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

            {/* Add More Students Button */}
            <div className="add-students-section" style={{ marginTop: 32 }}>
              <button className="add-more-btn" onClick={addMoreStudents}>
                + Add More Students
              </button>
            </div>

            {/* Instructions */}
            <div className="instructions">
              <h4>✅ Instructions:</h4>
              <ul>
                <li>Fill in <strong>Student Name</strong>, <strong>Subject</strong>, and <strong>Class</strong> first.</li>
                <li>Score each question (Q1-Q8) out of 5.</li>
                <li>The total score will be the sum of all Q1-Q8.</li>
                <li>Once you have entered all scores, <strong>press the Submit button</strong>.</li>
              </ul>
            </div>

            {/* Submit Button */}
            <div className="submit-section">
              <button
                className="submit-btn"
                onClick={handleSubmitEvaluations}
                disabled={submitting || students.some(s => !s.student_name)}
              >
                {submitting ? 'Submitting...' : `Submit ${students.length} Evaluation(s)`}
              </button>
              <button className="reset-btn" onClick={resetForm}>
                Start Over
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Only allow these subjects
  const subjectList = [
    { value: 'math', label: 'Math', color: '#4F8A8B' },
    { value: 'science', label: 'Science', color: '#F9B208' },
    { value: 'english', label: 'English', color: '#6A4FB6' }
  ];
  const subjectColors = {
    math: '#4F8A8B',
    science: '#F9B208',
    english: '#6A4FB6'
  };

  const renderEvaluationsTab = () => (
    <div className="evaluations-content">
      <div className="evaluations-header" style={{
        display: typeof window !== 'undefined' && window.innerWidth <= 576 ? 'block' : 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 24
      }}>
        <h3 style={{ margin: 0 }}>Your Past Evaluations</h3>
        {typeof window !== 'undefined' && window.innerWidth <= 576 ? (
          <div
            className="evaluations-controls"
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'stretch',
              gap: 12,
              width: '100%',
              marginTop: 12,
              boxSizing: 'border-box',
            }}
          >
            <select
              className="filter-select"
              value={selectedFilterClass}
              onChange={e => setSelectedFilterClass(e.target.value)}
              style={{ width: '100%' }}
            >
              <option value="">All Classes</option>
              <option value="pre-primary">Pre-Primary</option>
              <option value="primary">Primary</option>
              <option value="high-school">High School</option>
              <option value="higher-secondary">Higher Secondary</option>
            </select>
            <select
              className="filter-select"
              value={selectedFilterSubject}
              onChange={e => setSelectedFilterSubject(e.target.value)}
              style={{ width: '100%' }}
            >
              <option value="">All Subjects</option>
              {subjectList.map(subj => (
                <option key={subj.value} value={subj.value}>{subj.label}</option>
              ))}
            </select>
          </div>
        ) : (
          <div
            className="evaluations-controls"
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 16,
              width: 'auto',
              boxSizing: 'border-box',
            }}
          >
            <select
              className="filter-select"
              value={selectedFilterClass}
              onChange={e => setSelectedFilterClass(e.target.value)}
              style={{ width: 180 }}
            >
              <option value="">All Classes</option>
              <option value="pre-primary">Pre-Primary</option>
              <option value="primary">Primary</option>
              <option value="high-school">High School</option>
              <option value="higher-secondary">Higher Secondary</option>
            </select>
            <select
              className="filter-select"
              value={selectedFilterSubject}
              onChange={e => setSelectedFilterSubject(e.target.value)}
              style={{ width: 180 }}
            >
              <option value="">All Subjects</option>
              {subjectList.map(subj => (
                <option key={subj.value} value={subj.value}>{subj.label}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {evaluations.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px 0', color: '#888' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>📄</div>
          <h4>No evaluations found</h4>
          <p>Once you create student evaluations, they will appear here.</p>
        </div>
      ) : (
        <div className="evaluations-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 32 }}>
          {evaluations
            .filter(evaluation => {
              // Subject filter
              let subjectMatch = true;
              if (selectedFilterSubject) {
                const subj = (evaluation.subject || '').toLowerCase();
                if (selectedFilterSubject === 'math') subjectMatch = subj === 'math' || subj === 'mathematics';
                else if (selectedFilterSubject === 'science') subjectMatch = subj === 'science';
                else if (selectedFilterSubject === 'english') subjectMatch = subj === 'english';
                else subjectMatch = false;
              }
              // Class filter (normalize hyphens and spaces)
              let classMatch = true;
              if (selectedFilterClass) {
                const classVal = (evaluation.class_level || '').toLowerCase().replace(/-/g, ' ');
                const filterClass = selectedFilterClass.toLowerCase().replace(/-/g, ' ');
                classMatch = classVal === filterClass;
              }
              return subjectMatch && classMatch;
            })
            .map((evaluation) => {
              const subject = (evaluation.subject || '').toLowerCase();
              const subjectObj = subjectList.find(s => s.value === subject);
              let badgeColor = subjectObj ? subjectObj.color : '#bbb';
              let badgeLabel = subjectObj ? subjectObj.label : (subject.charAt(0).toUpperCase() + subject.slice(1));
              // Fix for legacy/variant subject values
              if (!subjectObj && (subject === 'mathematics')) {
                badgeLabel = 'Math';
                badgeColor = subjectColors['math'] || '#bbb';
              }
              if (!subjectObj && (subject === 'math')) {
                badgeLabel = 'Math';
                badgeColor = subjectColors['math'] || '#bbb';
              }
              return (
                <div key={evaluation.id} className="evaluation-card" style={{
                  background: '#fff',
                  borderRadius: 20,
                  boxShadow: '0 4px 18px 0 rgba(60,60,60,0.09)',
                  padding: 22,
                  marginBottom: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 210,
                  transition: 'box-shadow 0.2s',
                  border: '1.5px solid #f2f2f7',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                    <div
                      style={{
                        fontWeight: 700,
                        fontSize: '1.13em',
                        color: '#222',
                        maxWidth: '65%',
                        wordBreak: 'break-word',
                        whiteSpace: 'normal',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        lineHeight: '1.2em',
                        maxHeight: '2.4em',
                        cursor: 'pointer'
                      }}
                      title={evaluation.student_name}
                    >
                      {evaluation.student_name}
                    </div>
                    <span style={{
                      background: badgeColor,
                      color: '#fff',
                      borderRadius: 16,
                      padding: '3px 18px',
                      fontSize: '0.98em',
                      fontWeight: 600,
                      textTransform: 'capitalize',
                      letterSpacing: 0.2,
                      boxShadow: '0 1px 4px 0 rgba(0,0,0,0.07)'
                    }}>{badgeLabel}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span style={{ color: '#888', fontSize: '0.98em' }}>{new Date(evaluation.created_at).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}</span>
                    <span style={{ color: '#4F8A8B', fontWeight: 600, fontSize: '0.98em', background: '#f6f6fa', borderRadius: 8, padding: '2px 12px', marginLeft: 8 }}>{evaluation.class_level || 'N/A'}</span>
                  </div>
                  <div className="evaluation-scores" style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(4, 1fr)',
                    gap: 12,
                    marginBottom: 10
                  }}>
                    {[1,2,3,4,5,6,7,8].map(q => (
                      <div key={q} className="score-item" style={{
                        background: '#f6f6fa',
                        borderRadius: 10,
                        padding: '8px 0 4px 0',
                        textAlign: 'center',
                        fontSize: '0.97em',
                        fontWeight: 500,
                        border: '1px solid #ececf7',
                        minWidth: 60
                      }}>Q{q}<br /><span style={{ fontWeight: 700, fontSize: '1.05em' }}>{evaluation[`question_${q}_score`]}/5</span></div>
                    ))}
                  </div>
                  {(() => {
                    let score = (typeof evaluation.total === 'number' && !isNaN(evaluation.total)) ? evaluation.total :
                                (typeof evaluation.total_marks === 'number' && !isNaN(evaluation.total_marks)) ? evaluation.total_marks :
                                (typeof evaluation.overall_score === 'number' && !isNaN(evaluation.overall_score)) ? evaluation.overall_score : null;
                    let maxScore = (typeof evaluation.total === 'number' && !isNaN(evaluation.total)) ? 40 :
                                   (typeof evaluation.total_marks === 'number' && !isNaN(evaluation.total_marks)) ? 40 :
                                   (typeof evaluation.overall_score === 'number' && !isNaN(evaluation.overall_score)) ? 40 : null;
                    let badgeClass = score !== null && maxScore !== null
                      ? (score >= 32 ? 'excellent' : score >= 20 ? 'good' : 'needs-improvement')
                      : 'needs-improvement';
                    return (
                      <span className={`score-badge ${badgeClass}`} style={{ display: 'block', marginBottom: 8, fontWeight: 700, fontSize: '1em', borderRadius: 10, textAlign: 'center', padding: '7px 0', letterSpacing: 0.2 }}>
                        Overall: {(typeof evaluation.overall_score === 'number' && !isNaN(evaluation.overall_score))
                          ? evaluation.overall_score
                          : [1,2,3,4,5,6,7,8].reduce((sum, q) => sum + (parseInt(evaluation[`question_${q}_score`]) || 0), 0)
                        }/40
                      </span>
                    );
                  })()}
                  {evaluation.notes && (
                    <div className="evaluation-notes" style={{
                      background: '#f4f2fa',
                      borderRadius: 10,
                      padding: '8px 14px',
                      color: '#4F3A8B',
                      fontSize: '0.97em',
                      marginTop: 4,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8
                    }}>
                      <span style={{ fontWeight: 700, color: '#6A4FB6', marginRight: 6, fontSize: '1em' }}>📝 Notes:</span>
                      <span>{evaluation.notes}</span>
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      )}
    </div>
  );

  const renderProfileTab = () => (
    <div className="profile-content">
      <TeacherProfile teacher={teacher} onUpdate={fetchTeacherData} />
    </div>
  );

  return (
    <div className="teacher-dashboard">
      {/* Header */}
      <header className="dashboard-header">
        <div className="header-left">
          <h1>CurioKAARDS Teacher Portal</h1>
          <p>Student Evaluation Dashboard</p>
        </div>
        <div className="header-right">
          <div className="teacher-info">
            <span>Welcome, {teacher?.teacher_name || 'Teacher'}</span>
          </div>
          <button 
            className="logout-btn"
            onClick={handleLogout}
          >
            <span>Logout</span>
            <i>→</i>
          </button>
        </div>
      </header>

      {/* Navigation Tabs */}
      <nav className="dashboard-nav">
        <button 
          className={`nav-tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          📊 Overview
        </button>
        <button 
          className={`nav-tab ${activeTab === 'evaluation' ? 'active' : ''}`}
          onClick={() => setActiveTab('evaluation')}
        >
          📝 Create Evaluation
        </button>
        <button 
          className={`nav-tab ${activeTab === 'evaluations' ? 'active' : ''}`}
          onClick={() => setActiveTab('evaluations')}
        >
          📄 Past Evaluations
        </button>
        <button 
          className={`nav-tab ${activeTab === 'profile' ? 'active' : ''}`}
          onClick={() => setActiveTab('profile')}
        >
          👤 Profile
        </button>
      </nav>

      {/* Tab Content */}
      <div className="dashboard-content">
        {activeTab === 'overview' && renderOverviewTab()}
        {activeTab === 'evaluation' && renderEvaluationTab()}
        {activeTab === 'evaluations' && renderEvaluationsTab()}
        {activeTab === 'profile' && renderProfileTab()}
      </div>
    </div>
  );
};

// Simplified Teacher Profile Component
const TeacherProfile = ({ teacher, onUpdate }) => {
  const educationalBoardOptions = [
    'CBSE',
    'ICSE',
    'State Board',
    'International',
    'Anganwadi/Pre-primary',
    'Other'
  ];
  // Checkbox options
  const specializationOptions = ['English', 'Math', 'Science'];
  const levelsTaughtOptions = ['Pre-Primary', 'Primary', 'High School', 'Higher Secondary'];
  const [formData, setFormData] = useState({
    teacher_name: teacher?.teacher_name || '',
    email: teacher?.email || '',
    mobile_number: teacher?.mobile_number || '',
    specialization: Array.isArray(teacher?.specialization) ? teacher.specialization : (teacher?.specialization ? teacher.specialization.split(', ') : []),
    state: teacher?.state || '',
    teaching_experience: teacher?.teaching_experience || '',
    levels_taught: Array.isArray(teacher?.levels_taught) ? teacher.levels_taught : (teacher?.levels_taught ? teacher.levels_taught.split(', ') : []),
    educational_board: teacher?.educational_board || '',
    area_of_residence: teacher?.area_of_residence || '',
    postal_pin_code: teacher?.postal_pin_code || ''
  });
  const [originalData, setOriginalData] = useState({});
  const [loading, setLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (teacher) {
      const initialData = {
        teacher_name: teacher.teacher_name || '',
        email: teacher.email || '',
        mobile_number: teacher.mobile_number || '',
        specialization: Array.isArray(teacher.specialization) ? teacher.specialization : (teacher.specialization ? teacher.specialization.split(', ') : []),
        state: teacher.state || '',
        teaching_experience: teacher.teaching_experience || '',
        levels_taught: Array.isArray(teacher.levels_taught) ? teacher.levels_taught : (teacher.levels_taught ? teacher.levels_taught.split(', ') : []),
        educational_board: teacher.educational_board || '',
        area_of_residence: teacher.area_of_residence || '',
        postal_pin_code: teacher.postal_pin_code || ''
      };
      setFormData(initialData);
      setOriginalData(initialData);
      setHasChanges(false);
    }
  }, [teacher]);

  const checkForChanges = (newData) => {
    const changed = Object.keys(newData).some(key => {
      if (Array.isArray(newData[key]) && Array.isArray(originalData[key])) {
        return JSON.stringify(newData[key].sort()) !== JSON.stringify(originalData[key].sort());
      }
      return String(newData[key]).trim() !== String(originalData[key]).trim();
    });
    setHasChanges(changed);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    let newFormData;
    if (type === 'checkbox') {
      if (name === 'specialization') {
        // Always save 'Math', never 'Mathematics', and remove only the clicked subject
        let normalizedValue = value === 'Mathematics' ? 'Math' : value;
        if (checked) {
          // Add if not present
          newFormData = {
            ...formData,
            [name]: Array.from(new Set([...formData[name].filter(item => item !== 'Mathematics'), normalizedValue]))
          };
        } else {
          // Remove only the unchecked subject
          newFormData = {
            ...formData,
            [name]: formData[name].filter(item => item !== normalizedValue && item !== (value === 'Math' ? 'Mathematics' : ''))
          };
        }
      } else {
        newFormData = {
          ...formData,
          [name]: checked 
            ? [...formData[name], value]
            : formData[name].filter(item => item !== value)
        };
      }
    } else {
      newFormData = {
        ...formData,
        [name]: value
      };
    }

    setFormData(newFormData);
    checkForChanges(newFormData);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      // Send arrays as arrays for PostgreSQL
      const dataToSend = {
        ...formData,
        specialization: formData.specialization,
        levels_taught: formData.levels_taught
      };
      const response = await axios.put('/api/teacher/profile', dataToSend, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success('Profile updated successfully');
        setOriginalData(formData);
        setHasChanges(false);
        setShowConfirmation(true);
        setTimeout(() => setShowConfirmation(false), 3000);
        onUpdate();
      } else {
        toast.error('Failed to update profile');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Error updating profile');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFormData(originalData);
    setHasChanges(false);
  };

  return (
    <div className="teacher-profile-container">
      {/* Header */}
      <div className="profile-edit-header">
        <div className="header-content">
          <h2>Edit Profile</h2>
          <p>Update your professional information</p>
        </div>
        {hasChanges && (
          <div className="changes-indicator">
            <span className="changes-badge">• Unsaved changes</span>
          </div>
        )}
      </div>

      {/* Profile Form */}
      <form onSubmit={handleSubmit} className="profile-edit-form">
        {/* Personal Information Section */}
        <div className="form-section">
          <div className="section-header">
            <div className="section-icon">👤</div>
            <div className="section-title">
              <h3>Personal Information</h3>
              <p>Basic details about you</p>
            </div>
          </div>
          <div className="form-fields">
            <div className="form-group">
              <label htmlFor="teacher_name">Full Name *</label>
              <input
                type="text"
                id="teacher_name"
                name="teacher_name"
                value={formData.teacher_name}
                onChange={handleInputChange}
                placeholder="Enter your full name"
                required
                style={{ width: 'fit-content' }}
              />
            </div>
            <div className="form-group">
              <label htmlFor="email">Email Address *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="your.email@example.com"
                required
                style={{ width: 'fit-content' }}
              />
            </div>
            <div className="form-group">
              <label htmlFor="mobile_number">Mobile Number</label>
              <input
                type="tel"
                id="mobile_number"
                name="mobile_number"
                value={formData.mobile_number}
                onChange={handleInputChange}
                placeholder="Enter your mobile number"
                style={{ width: 'fit-content' }}
              />
            </div>
          </div>
        </div>

        {/* Address Information Section */}
        <div className="form-section">
          <div className="section-header">
            <div className="section-icon">📍</div>
            <div className="section-title">
              <h3>Address Information</h3>
              <p>Your location details</p>
            </div>
          </div>
          <div className="form-fields">
            <div className="form-group">
              <label htmlFor="area_of_residence">Area of Residence</label>
              <input
                type="text"
                id="area_of_residence"
                name="area_of_residence"
                value={formData.area_of_residence}
                onChange={handleInputChange}
                placeholder="City, Area, Locality"
                style={{ width: 'fit-content' }}
              />
            </div>
            <div className="form-group">
              <label htmlFor="state">State</label>
              <input
                type="text"
                id="state"
                name="state"
                value={formData.state}
                onChange={handleInputChange}
                placeholder="Enter your state"
                style={{ width: 'fit-content' }}
              />
            </div>
            <div className="form-group">
              <label htmlFor="postal_pin_code">Postal/PIN Code</label>
              <input
                type="text"
                id="postal_pin_code"
                name="postal_pin_code"
                value={formData.postal_pin_code}
                onChange={handleInputChange}
                placeholder="411001"
                style={{ width: 'fit-content' }}
              />
            </div>
          </div>
        </div>

        {/* Professional Information Section */}
        <div className="form-section">
          <div className="section-header">
            <div className="section-icon">🎓</div>
            <div className="section-title">
              <h3>Professional Information</h3>
              <p>Your educational and teaching background</p>
            </div>
          </div>
          <div className="form-fields">
            <div className="form-group">
              <label>Specialization</label>
              <div className="checkbox-group">
                {specializationOptions.map(option => (
                  <label key={option} className="checkbox-label" style={{ width: 'fit-content' }}>
                    <input
                      type="checkbox"
                      name="specialization"
                      value={option}
                      checked={Array.isArray(formData.specialization) ? formData.specialization.includes(option) : false}
                      onChange={handleInputChange}
                      style={{ width: 'fit-content' }}
                    />
                    <span className="checkbox-text">{option}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="educational_board">Educational Board</label>
              <select
                id="educational_board"
                name="educational_board"
                value={formData.educational_board}
                onChange={handleInputChange}
                required
                style={{ width: 'fit-content' }}
              >
                <option value="">Select Board</option>
                {educationalBoardOptions.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
            <>
              <div className="form-group">
                <label htmlFor="teaching_experience">Teaching Experience (years)</label>
                <input
                type="number"
                id="teaching_experience"
                name="teaching_experience"
                value={formData.teaching_experience}
                onChange={handleInputChange}
                placeholder="0"
                min="0"
                max="50"
                style={{ width: 'fit-content' }}
              />
              </div>
              <div className="form-group">
                <label>Levels Taught</label>
                <div className="checkbox-group">
                  {levelsTaughtOptions.map(option => (
                    <label key={option} className="checkbox-label" style={{ width: 'fit-content' }}>
                      <input
                        type="checkbox"
                        name="levels_taught"
                        value={option}
                        checked={Array.isArray(formData.levels_taught) ? formData.levels_taught.includes(option) : false}
                        onChange={handleInputChange}
                        style={{ width: 'fit-content' }}
                      />
                      <span className="checkbox-text">{option}</span>
                    </label>
                  ))}
                </div>
              </div>
            </>
          </div>
        </div>

        {/* Confirmation Message (moved here) */}
        {showConfirmation && (
          <div className="confirmation-message">
            <div className="confirmation-content">
              <div className="confirmation-icon">✅</div>
              <div className="confirmation-text">
                <h4>Profile Updated Successfully!</h4>
                <p>Your information has been saved and updated.</p>
              </div>
            </div>
          </div>
        )}

        {/* Form Actions */}
        <div className="form-actions">
          <button 
            type="button" 
            className="btn-secondary"
            onClick={handleReset}
            disabled={loading || !hasChanges}
          >
            Reset Changes
          </button>
          <button 
            type="submit" 
            className="btn-primary"
            disabled={loading || !hasChanges}
          >
            {loading ? (
              <span className="loading-text">
                <span className="spinner"></span>
                Saving...
              </span>
            ) : (
              'Save Changes'
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

export default TeacherDashboard;
